// Decoding: the full-resolution bitmap for export, a ≤2048 px working copy for the stage.

export const WORKING_MAX = 2048;
export const MAX_BYTES = 8 * 1024 * 1024;

export interface LoadedImage {
  full: ImageBitmap | HTMLImageElement;
  working: HTMLCanvasElement;
  workingScale: number; // working px per original px
  width: number;
  height: number;
  fakeTransparency: boolean; // a "transparent" checkerboard baked into the pixels
}

export function isHeic(file: File): boolean {
  return /image\/hei[cf]/.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

export const SUPPORTED = ['JPEG', 'PNG', 'HEIC'];

/**
 * The file's real format, from its first bytes. File.type cannot decide this: Android pickers
 * leave it empty, and `image/*` passes WebP and AVIF — which every browser shows and the API
 * stores none of, so the customer only found out when the finished design failed to save.
 * Anything unrecognised falls back to the browser's own type, which names BMP, TIFF and SVG.
 */
export async function sniff(file: File): Promise<string> {
  const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const s = Array.from(b, (x) => String.fromCharCode(x)).join('');
  if (s.startsWith('\xff\xd8\xff')) return 'JPEG';
  if (s.startsWith('\x89PNG')) return 'PNG';
  if (s.slice(4, 8) === 'ftyp') {
    const brand = s.slice(8, 12).trim();
    return /^(heic|heix|mif1|heif|hevc)$/.test(brand) ? 'HEIC' : brand.toUpperCase();
  }
  if (s.startsWith('RIFF') && s.slice(8, 12) === 'WEBP') return 'WEBP';
  if (s.startsWith('GIF8')) return 'GIF';
  return file.type.replace(/^image\//, '').toUpperCase() || 'okänt format';
}

export async function loadImage(blob: Blob): Promise<LoadedImage> {
  let full: ImageBitmap | HTMLImageElement;
  try {
    full = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch {
    full = await viaImgElement(blob); // browsers apply EXIF to <img> by default
  }
  const width = full.width, height = full.height;
  const workingScale = Math.min(1, WORKING_MAX / Math.max(width, height));
  const working = document.createElement('canvas');
  working.width = Math.round(width * workingScale);
  working.height = Math.round(height * workingScale);
  const ctx = working.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(full, 0, 0, working.width, working.height);
  // Sixteen rows and sixteen columns spread over the whole picture: the grid may only show in a
  // band between the subject and a plain white margin. Two hits make a grid.
  const { width: w, height: h } = working;
  const lines = Array.from({ length: 16 }, (_, i) => (i + 0.5) / 16).flatMap((f) => [
    ctx.getImageData(0, Math.floor(h * f), w, 1).data, ctx.getImageData(Math.floor(w * f), 0, 1, h).data,
  ]);
  const fakeTransparency = lines.filter(checkerLine).length >= 2;
  return { full, working, workingScale, width, height, fakeTransparency };
}

/**
 * One RGBA pixel line: does it cross the white / light-grey checkerboard that image sites show
 * behind "transparent" pictures? Six squares in a row, alternating and about equally long, is
 * the signature. Real transparency has alpha 0 and never matches.
 * The grey is anything from #ccc to a faint #eee; 244..247 is a blurred edge between the two
 * and stays with the run it is in, so JPEG noise on a white backdrop cannot flicker into squares.
 * ponytail: light grids only; the rare dark-grey variant goes unwarned.
 */
export function checkerLine(rgba: Uint8ClampedArray): boolean {
  let chain = 0, prevClass = 0, prevLen = 0, cls = 0, len = 0;
  for (let i = 0; i <= rgba.length; i += 4) {
    const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
    const neutral = i < rgba.length && rgba[i + 3] === 255 && Math.max(r, g, b) - Math.min(r, g, b) <= 12 && g >= 170;
    const c = !neutral ? 0 : g >= 248 ? 1 : g <= 243 ? 2 : cls || 1; // 0 other, 1 white, 2 light grey
    if (c === cls) { len++; continue; }
    // A run just ended: it extends the chain if it is a square like the one before it.
    const square = cls !== 0 && len >= 3;
    const follows = square && prevClass !== 0 && prevClass !== cls && len / prevLen > 0.7 && len / prevLen < 1.43;
    chain = follows ? chain + 1 : square ? 1 : 0;
    if (chain >= 6) return true;
    [prevClass, prevLen] = square ? [cls, len] : [0, 0];
    [cls, len] = [c, 1];
  }
  return false;
}

function viaImgElement(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url;
  });
}
